import type { Restaurant } from "@/API";
import Badge from "@/components/Badge";
import RemoveRestaurantDialog from "@/components/dialogs/RemoveRestaurantDialog";
import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import { getColorKrunshScore } from "@/lib/utils";
import { getNumberOfAddressesAfterRemovingUserRestaurant } from "@/store/numberOfAddressesAfterRemovingUserRestaurantState";
import { setPriceId, setUpdatePriceAction } from "@/store/paymentActionState";
import { userPlanState } from "@/store/userPlanState";
import {
  resetRemoveUserRestaurantsState,
  userRemoveRestaurantState,
} from "@/store/userRemoveRestaurantState";
import { userRestaurantsState } from "@/store/userRestaurantsState";
import {
  BikeIcon,
  ChevronDown,
  Ellipsis,
  Eye,
  Search,
  StarIcon,
  StoreIcon,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

type SortOption = "krunshScore_desc" | "krunshScore_asc" | "name_asc";

const SORT_OPTIONS = [
  {
    label: "sort_option_score_desc",
    value: "krunshScore_desc",
  },
  {
    label: "sort_option_score_asc",
    value: "krunshScore_asc",
  },
  {
    label: "sort_option_name_asc",
    value: "name_asc",
  },
];

const Restaurants = () => {
  const { t } = useTranslation();
  const { sm } = useDetectSizeDevice();
  const navigate = useNavigate();

  const { data: restaurants } = userRestaurantsState();
  const { data: restaurantIsRemoved } = userRemoveRestaurantState();
  const { plan } = userPlanState();

  const [sortOption, setSortOption] = useState<SortOption>("krunshScore_desc");
  const [search, setSearch] = useState("");
  const [dialogToRemoveRestaurantOpen, setDialogToRemoveRestaurantOpen] =
    useState<Restaurant | null>(null);

  useEffect(() => {
    if (restaurantIsRemoved) {
      toast.success(t("restaurant_removed"), {
        duration: 2000,
      });
      resetRemoveUserRestaurantsState();
      setDialogToRemoveRestaurantOpen(null);
    }
  }, [restaurantIsRemoved]);

  if (restaurants === null) return null;

  if (restaurants.length === 0)
    return (
      <div
        style={{
          height: sm
            ? "calc(100dvh - var(--height-header))"
            : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
        }}
        className="bg-[#f7f8f9] p-[16px] sm:p-[40px] overflow-y-auto flex flex-col gap-[32px]"
      >
        <div className="bg-[white] p-[16px] sm:py-[40px] sm:px-[80px] flex flex-col justify-center items-center mt-[24px] rounded-[12px]">
          <div className="h-[40px] w-[40px] bg-[#F1F2F4] rounded-[12px] flex justify-center items-center mb-[12px]">
            <StoreIcon color="#43546F" height={24} width={24} />
          </div>
          <Text
            fontSize={16}
            fontWeight={600}
            color="#44546F"
            className="text-center"
          >
            {t("my_stores_empty_state")}
          </Text>
          <Text
            fontSize={14}
            fontWeight={400}
            color="#44546F"
            className="text-center mt-[12px]"
          >
            {t("my_stores_empty_state_description")}
          </Text>

          <Link className="mt-[32px]" to="/find-restaurant">
            <Button>{t("add_my_stores")}</Button>
          </Link>
        </div>
      </div>
    );

  const restaurantWithBestKrunshScore = restaurants.reduce(
    (best: Restaurant, restaurant: Restaurant) => {
      if (best === null || restaurant.krunshScore > best.krunshScore) {
        return restaurant;
      }
      return best;
    },
    restaurants[0],
  );

  const restaurantWithWorstKrunshScore = restaurants.reduce(
    (best: Restaurant, restaurant: Restaurant) => {
      if (best === null || restaurant.krunshScore < best.krunshScore) {
        return restaurant;
      }
      return best;
    },
    restaurants[0],
  );

  const ratingsOfRestaurantsWithRatings = restaurants
    .filter((restaurant) => restaurant.rating && restaurant.rating !== "0")
    .map((restaurant) => Number(restaurant.rating));

  const countRestaurantsWithoutPromotion = restaurants.filter(
    (restaurant) => restaurant.promotionCount === 0,
  ).length;

  let restaurantsTodiplay = restaurants;

  if (search) {
    restaurantsTodiplay = restaurantsTodiplay.filter((restaurant) =>
      restaurant.name.toLowerCase().includes(search.toLowerCase()),
    );
  }

  switch (sortOption) {
    case "krunshScore_desc":
      restaurantsTodiplay = restaurantsTodiplay.sort(
        (a, b) => b.krunshScore - a.krunshScore,
      );
      break;
    case "krunshScore_asc":
      restaurantsTodiplay = restaurantsTodiplay.sort(
        (a, b) => a.krunshScore - b.krunshScore,
      );
      break;
    case "name_asc":
      restaurantsTodiplay = restaurantsTodiplay.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      break;
  }

  return (
    <>
      <RemoveRestaurantDialog
        open={dialogToRemoveRestaurantOpen !== null}
        onClose={() => setDialogToRemoveRestaurantOpen(null)}
        restaurant={dialogToRemoveRestaurantOpen ?? undefined}
      />

      <div
        style={{
          height: sm
            ? "calc(100dvh - var(--height-header))"
            : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
        }}
        className="bg-[#f7f8f9] p-[16px] sm:p-[40px] overflow-y-auto flex flex-col gap-[32px] w-[100%]"
      >
        {/* Header */}
        <div className="flex justify-between">
          <Text fontSize={32} fontWeight={600}>
            {t("my_stores_count", { count: restaurants.length })}
          </Text>
          <Link to="/find-restaurant">
            <Button>{sm ? t("add_my_stores") : t("add")}</Button>
          </Link>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-[16px] sm:overflow-x-auto sm:min-h-[88px]">
          {/* KPI 1 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px] ">
            <Text fontSize={12} fontWeight={400}>
              {t("best_krunsh_score")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <div
                className="flex items-center justify-center h-[32px] w-[32px] min-h-[32px] min-w-[32px] rounded-[50%]"
                style={{
                  border: `4px solid ${getColorKrunshScore(restaurantWithBestKrunshScore.krunshScore)}`,
                }}
              >
                <Text
                  fontSize={14}
                  fontWeight={600}
                  color={getColorKrunshScore(
                    restaurantWithBestKrunshScore.krunshScore,
                  )}
                >
                  {restaurantWithBestKrunshScore.krunshScore}
                </Text>
              </div>
              <img
                className="h-[24px] w-[24px] rounded-[50%] object-cover"
                src={`/partner/${restaurantWithBestKrunshScore.platform.toLowerCase()}.png`}
                alt={restaurantWithBestKrunshScore.platform}
              />
              <Text fontSize={14} fontWeight={600}>
                {restaurantWithBestKrunshScore.name}
              </Text>
            </div>
          </div>
          {/* KPI 2 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px] ">
            <Text fontSize={12} fontWeight={400}>
              {t("worst_krunsh_score")}
            </Text>
            <div className="flex items-center gap-[8px]">
              <div
                className="flex items-center justify-center h-[32px] w-[32px] min-h-[32px] min-w-[32px] rounded-[50%]"
                style={{
                  border: `4px solid ${getColorKrunshScore(restaurantWithWorstKrunshScore.krunshScore)}`,
                }}
              >
                <Text
                  fontSize={14}
                  fontWeight={600}
                  color={getColorKrunshScore(
                    restaurantWithWorstKrunshScore.krunshScore,
                  )}
                >
                  {restaurantWithWorstKrunshScore.krunshScore}
                </Text>
              </div>
              <img
                className="h-[24px] w-[24px] rounded-[50%] object-cover"
                src={`/partner/${restaurantWithWorstKrunshScore.platform.toLowerCase()}.png`}
                alt={restaurantWithWorstKrunshScore.platform}
              />
              <Text fontSize={14} fontWeight={600}>
                {restaurantWithWorstKrunshScore.name}
              </Text>
            </div>
          </div>
          {/* KPI 3 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px] ">
            <Text fontSize={12} fontWeight={400}>
              {t("restaurant_average_score")}
            </Text>
            <Text fontSize={23} fontWeight={600} style={{ lineHeight: "32px" }}>
              {ratingsOfRestaurantsWithRatings.length > 0
                ? (
                    ratingsOfRestaurantsWithRatings.reduce(
                      (prevValue, currentValue) => prevValue + currentValue,
                    ) / ratingsOfRestaurantsWithRatings.length
                  ).toFixed(2)
                : t("not_rated")}
            </Text>
          </div>
          {/* KPI 4 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px] ">
            <Text fontSize={12} fontWeight={400}>
              {t("to_monitor")}
            </Text>

            <Text fontSize={23} fontWeight={600} style={{ lineHeight: "32px" }}>
              {
                restaurants.filter((restaurant) => restaurant.krunshScore < 60)
                  .length
              }
            </Text>
          </div>
          {/* KPI 5 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px] ">
            <Text fontSize={12} fontWeight={400}>
              {t("without_promotion")}
            </Text>

            <Text fontSize={23} fontWeight={600} style={{ lineHeight: "32px" }}>
              {countRestaurantsWithoutPromotion}
            </Text>
          </div>
        </div>

        {/* Research and sort  */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-[16px]">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            className="w-[100%] sm:w-[446px]"
            classNameInput="rounded-[72px] border-none"
            placeholder={t("find_restaurant")}
            endIcon={Search}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondaryOutlined">
                <Text fontSize={14} fontWeight={600}>
                  {t(
                    SORT_OPTIONS.find(({ value }) => value === sortOption)
                      ?.label ?? "",
                  )}
                </Text>
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuGroup>
                {SORT_OPTIONS.map(({ label, value }) => (
                  <DropdownMenuItem
                    onClick={() => setSortOption(value as SortOption)}
                    key={value}
                  >
                    <Text fontSize={14} fontWeight={600}>
                      {t(label)}
                    </Text>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Restaurant List  */}
        <div className="flex flex-col gap-[12px]">
          {restaurantsTodiplay.map((restaurant) => (
            <div
              onClick={() => {
                navigate(`/restaurant/${restaurant.id}`);
              }}
              key={restaurant.id}
              className="flex items-center rounded-[12px] bg-[white] justify-between p-[16px] cursor-pointer"
            >
              <div
                className="flex items-center gap-[12px]"
                style={
                  sm
                    ? undefined
                    : {
                        maxWidth: "calc(100% - 40px)",
                      }
                }
              >
                <div
                  className="flex items-center justify-center h-[32px] w-[32px] min-w-[32px] sm:h-[40px] sm:w-[40px] sm:min-w-[40px] rounded-[50%]"
                  style={{
                    border: `4px solid ${getColorKrunshScore(restaurant.krunshScore)}`,
                  }}
                >
                  <Text
                    fontSize={sm ? 16 : 14}
                    fontWeight={600}
                    color={getColorKrunshScore(restaurant.krunshScore)}
                  >
                    {restaurant.krunshScore}
                  </Text>
                </div>
                <img
                  className="h-[32px] w-[32px] rounded-[50%] object-cover"
                  src={restaurant?.imageUrl ?? undefined}
                  alt={restaurant.name}
                />
                <img
                  className="h-[32px] w-[32px] ml-[-28px] rounded-[50%] object-cover"
                  src={`/partner/${restaurant.platform.toLowerCase()}.png`}
                  alt={restaurant.platform}
                />

                <div
                  className="flex flex-col gap-[8px]"
                  style={
                    sm
                      ? undefined
                      : {
                          maxWidth: "calc(100% - 130px)",
                        }
                  }
                >
                  <Text fontSize={16} fontWeight={600} className="truncate">
                    {restaurant.name}
                  </Text>
                  <Text fontSize={14} fontWeight={400} className="truncate">
                    {restaurant.address}
                  </Text>
                </div>
              </div>

              <div className="flex items-center gap-[16px]">
                {sm && (
                  <div className="mr-[8px] flex items-center gap-[4px]">
                    <Badge>
                      {restaurant.rating === "0" ? (
                        <Text fontWeight={600} fontSize={14}>
                          {t("not_rated")}
                        </Text>
                      ) : (
                        <>
                          <StarIcon height={16} width={16} color="#758195" />
                          <Text fontWeight={600} fontSize={14}>
                            {`${restaurant.rating}/5`}
                          </Text>
                        </>
                      )}
                    </Badge>
                    <Badge>
                      <BikeIcon height={16} width={16} color="#758195" />
                      <Text fontWeight={600} fontSize={14}>
                        {`${restaurant.deliveryTime} min`}
                      </Text>
                    </Badge>
                  </div>
                )}
                {sm ? (
                  <>
                    <Link to={`/restaurant/${restaurant.id}`}>
                      <button className="bg-[#F7F8F9] rounded-[8px] p-[8px]">
                        <Eye height={16} width={16} color="#758195" />
                      </button>
                    </Link>
                    <button
                      className="bg-[#AE2E24] rounded-[8px] p-[8px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (plan && plan.name === "Radar") {
                          getNumberOfAddressesAfterRemovingUserRestaurant(
                            restaurant.id,
                          );
                          setPriceId(plan.prices[0]?.id ?? "");
                          setUpdatePriceAction({
                            action: "delete_restaurant",
                            restaurantsId: [restaurant.id],
                            quantity: 1,
                          });
                        } else {
                          setDialogToRemoveRestaurantOpen(restaurant);
                        }
                      }}
                    >
                      <Trash2 height={16} width={16} color="white" />
                    </button>
                  </>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className="bg-[#F7F8F9] rounded-[8px] p-[8px]"
                      >
                        <Ellipsis height={16} width={16} color="#758195" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="mr-[24px]">
                      <DropdownMenuGroup>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/restaurant/${restaurant.id}`);
                          }}
                        >
                          <button className="bg-[#F7F8F9] rounded-[8px] p-[8px]">
                            <Eye height={16} width={16} color="#758195" />
                          </button>
                          <Text>{t("see")}</Text>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            if (plan && plan.name === "Radar") {
                              getNumberOfAddressesAfterRemovingUserRestaurant(
                                restaurant.id,
                              );
                              setPriceId(plan.prices[0]?.id ?? "");
                              setUpdatePriceAction({
                                action: "delete_restaurant",
                                restaurantsId: [restaurant.id],
                                quantity: 1,
                              });
                            } else {
                              setDialogToRemoveRestaurantOpen(restaurant);
                            }
                          }}
                        >
                          <button className="bg-[#AE2E24] rounded-[8px] p-[8px]">
                            <Trash2 height={16} width={16} color="white" />
                          </button>
                          <Text>{t("delete")}</Text>
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Restaurants;
